/**
 * Internal env var naming for "hidden" credential providers.
 *
 * For git providers (github / gitlab / bitbucket) the AI must NOT
 * know the env var name that holds the token — knowing it lets the
 * AI write `https://x-access-token:$VAR@host/...` URLs that persist
 * to .git/config (which we don't want). Instead, the worker injects
 * tokens under unique internal names, and a credential helper baked
 * into the sandbox container reads them via a routes manifest.
 *
 * The naming MUST be identical on both sides:
 *   - worker (cred_to_env tool) generates the name when building
 *     envMapping for the session_credential row,
 *   - browser (ChatPanel addPending) generates the same name from
 *     the same inputs so the row's env_mapping is consistent.
 *
 * Identical inputs → identical name. We use sessionId + requestId
 * (both known to both sides) so two different cred_to_env calls in
 * the same session get distinct env vars (no collision when the AI
 * binds two github creds for two different repos).
 *
 * Naming format: `__PAPAN_CRED_<PROVIDER>_<id-tail>` where:
 *   - leading `__PAPAN_` clearly marks "internal — don't use as a
 *     command arg"
 *   - PROVIDER is uppercase (`GITHUB`, `GITLAB`, `BITBUCKET`) so a
 *     human reading `env` output can quickly tell what kind of cred
 *     it routes to
 *   - id-tail is the requestId stripped of its `call_` prefix and
 *     truncated, giving collision-resistance per binding (requestId
 *     is a nanoid; even 8 alphanumeric chars give >1e14 combinations).
 */

/** Providers whose tokens are routed via the sandbox credential helper
 *  (no env var name surfaced to the AI). Keys must match what cred_to_env
 *  receives in `args.provider`. Values are the host the helper will see
 *  in git's stdin — used by the worker to build the routes manifest. */
export const HIDDEN_PROVIDER_HOSTS: Record<string, string> = {
  github: 'github.com',
  gitlab: 'gitlab.com',
  bitbucket: 'bitbucket.org',
}

export function isHiddenProvider(provider: string | undefined): boolean {
  return !!provider && provider in HIDDEN_PROVIDER_HOSTS
}

/** Generates the per-binding internal env var name. Identical output
 *  for identical (provider, requestId) inputs — the worker and browser
 *  both call this with the same args so the session_credential row
 *  carries one consistent env var name across both sides. */
export function hiddenCredEnvName(provider: string, requestId: string): string {
  const provUpper = provider.toUpperCase()
  // requestId is typically `call_<22-char-nanoid>`. Strip the prefix
  // for cleanliness, then take the first 12 alphanumeric chars —
  // long enough to avoid collisions within a session, short enough
  // to keep env output readable. Sanitize to `[A-Za-z0-9_]` to make
  // the result a valid POSIX identifier (env var rules).
  const id = requestId.replace(/^call_/, '').replace(/[^A-Za-z0-9]/g, '').slice(0, 12)
  return `__PAPAN_CRED_${provUpper}_${id || 'X'}`
}
